import React from "react";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import Badge from "@material-ui/core/Badge";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useStyles } from "../style";
import { getActiveChannel, getChannelPostList, setSelectedPostMenu } from "../../../../redux/actions/programAction";
import DangerAlertIcon from "../../../../components/Icons/DangerAlertIcon";
import PostList from "./PostList";
import ChannelMenu from "./Menu";
import { RootState } from "../../../../redux/store";
import { handleDrawer } from "../../../../redux/actions/drawerAction";
import { useIsMobile } from "../../../../util/hooks";

const invertColor = (hex: string) => {
  let col = (hex || "#ffffff").replace(/^#/, "");
  if (col.length === 3) col = col.split("").map((c) => c + c).join("");
  const num = parseInt(col || "ffffff", 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

const ChannelListItem = ({
  item,
  activeChannel,
  setActiveChannel,
  programId,
  userId,
  communityIdentifier,
  channelRole = 'viewer',
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const selectedPostMenu = useSelector((state: RootState) => state.programReducer.selectedPostMenu);
  const isMobile = useIsMobile();
  const [modDialogOpen, setModDialogOpen] = React.useState(false);

  // 🎨 Pull community theme (defaults if missing)
  const singleProgram = useSelector((state: RootState) => state.programReducer.singleProgram);
  const communityTheme = React.useMemo(() => {
    const community = singleProgram?.communities?.find(
      (c: any) => c?.communityIdentifier === communityIdentifier
    );
    return {
      mainColor: community?.communityStyle?.mainColor || "#1976d2",
      secondaryColor: community?.communityStyle?.secondaryColor || "#ffffff",
      fontStyle: community?.communityStyle?.fontStyle || "Arial",
    };
  }, [singleProgram, communityIdentifier]);

  const fgOnSecondary = React.useMemo(
    () => invertColor(communityTheme.secondaryColor),
    [communityTheme.secondaryColor]
  );

  const menuItems = [
    {
      label: "All Posts",
      onClick: () => {
        dispatch(getChannelPostList(programId, userId, communityIdentifier, item.channelIdentifier));
        dispatch(setSelectedPostMenu(null));
      },
      className: selectedPostMenu === null ? "active" : "",
    },
    {
      label: "Archived Posts",
      onClick: () => {
        dispatch(
          getChannelPostList(programId, userId, communityIdentifier, item.channelIdentifier, true)
        );
        dispatch(setSelectedPostMenu("archivedPost"));
      },
      className: selectedPostMenu === "archivedPost" ? "active" : "",
    }
  ];

  const isSelected = activeChannel === item.channelIdentifier;

  return (
    <>
      <ListItem
        button
        disableRipple
        className={`${classes.listItem} ${isSelected ? classes.activeChannel : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          if (isMobile) {
            dispatch(handleDrawer(false));
          }

          dispatch(getActiveChannel(item));

          const themeData =
            item.channelStyle ||
            item.channelPostingSettings?.theme ||
            item.channelFormSettings?.theme ||
            {
              mainColor: "#1976d2",
              secondaryColor: "#ffffff",
              headerImage: "",
              fontStyle: "Arial",
            };
          dispatch({
            type: "SET_CHANNEL_THEME",
            channelId: item.channelIdentifier,
            payload: themeData,
          });

          setActiveChannel(item.channelIdentifier);
          history.push(`/user/channel?channelIdentifier=${item.channelIdentifier}`);
        }}
        // 🎨 Colors only: subtle selected state, readable text, font
        style={{
          backgroundColor: isSelected
            ? `${communityTheme.mainColor}1A` /* ~10% tint */
            : "transparent",
          borderLeft: isSelected ? `3px solid ${communityTheme.mainColor}` : "3px solid transparent",
          color: fgOnSecondary,
          fontFamily: communityTheme.fontStyle,
        }}
      >
        {item.isOpen && <ChannelMenu menuItems={menuItems} />}

        <div style={{ marginRight: "15px" }}>
          {item.badge ? (
            <Badge badgeContent={item.badgeCount || 0} max={99}>
              <span style={{ color: communityTheme.mainColor }}>{item.icon}</span>
            </Badge>
          ) : (
            <ListItemIcon style={{ color: communityTheme.mainColor }}>{item.icon}</ListItemIcon>
          )}
        </div>

        <ListItemText
          className={classes.listItemText}
          primary={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{item.label}</span>
              {(channelRole === 'admin' || channelRole === 'moderator') && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setModDialogOpen(true);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    backgroundColor: `${communityTheme.mainColor}20`,
                    border: `1px solid ${communityTheme.mainColor}60`,
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: communityTheme.mainColor,
                    fontFamily: communityTheme.fontStyle,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${communityTheme.mainColor}35`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${communityTheme.mainColor}20`;
                  }}
                  title={`Click to learn about ${channelRole} access`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginRight: '4px' }}
                  >
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                  </svg>
                  {channelRole === 'admin' ? 'ADMIN' : 'MOD'}
                </span>
              )}
            </div>
          }
          secondary={item.description}
          primaryTypographyProps={{ style: { color: fgOnSecondary, fontFamily: communityTheme.fontStyle } }}
          secondaryTypographyProps={{ style: { color: `${fgOnSecondary}CC`, fontFamily: communityTheme.fontStyle } }}
        />

        {item.alert && (
          <div style={{ marginBottom: "30px" }}>
            <DangerAlertIcon />
          </div>
        )}

        <div
          style={{ display: "flex" }}
          onClick={(event) => {
            event.stopPropagation();
            item.onClick();
          }}
        >
          <div className={classes.divider} style={{ background: `${fgOnSecondary}33` }} />
          <div>
            {item.isOpen ? (
              <ExpandLessIcon style={{ color: communityTheme.mainColor }} />
            ) : (
              <ExpandMoreIcon style={{ color: communityTheme.mainColor }} />
            )}
          </div>
        </div>
      </ListItem>

      <Collapse in={item.isOpen} timeout="auto" unmountOnExit>
        <PostList item={item} />
      </Collapse>

      {/* Moderator/Admin Info Dialog */}
      <Dialog
        open={modDialogOpen}
        onClose={() => setModDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{
          color: communityTheme.mainColor,
          fontFamily: communityTheme.fontStyle,
          fontWeight: 600,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
            </svg>
            {channelRole === 'admin' ? 'Administrator Access' : 'Moderator Access'}
          </div>
        </DialogTitle>
        <DialogContent>
          <div style={{ fontFamily: communityTheme.fontStyle, lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              You have <strong>{channelRole === 'admin' ? 'administrator' : 'moderator'} privileges</strong> for the <strong>{item.label}</strong> channel.
            </p>
            <p>
              As {channelRole === 'admin' ? 'an administrator' : 'a moderator'}, you can:
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li>Manage and moderate posts in this channel</li>
              <li>Review and approve content</li>
              <li>Manage channel members and permissions</li>
              <li>Access special {channelRole === 'admin' ? 'administration' : 'moderation'} tools</li>
              {channelRole === 'admin' && (
                <>
                  <li>Configure channel settings and themes</li>
                  <li>Assign moderators to channels</li>
                </>
              )}
            </ul>
            <p style={{ marginBottom: 0, fontSize: '14px', color: '#666' }}>
              Use these privileges responsibly to maintain a healthy community.
            </p>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModDialogOpen(false)}
            style={{
              color: communityTheme.mainColor,
              fontFamily: communityTheme.fontStyle,
              fontWeight: 600,
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChannelListItem;
